import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button } from "@jugl-web/ui-kit";
import { Alert } from "@jugl-web/ui-components/cross-platform";
import { PendingInvoice } from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import useEntity from "@web-src/features/app/hooks/useEntity";
import { useSubscriptionV2 } from "../../../../providers/SubscriptionV2Provider";
import { CreditCardBase } from "../CreditCardBase/CreditCardBase";
import { PendingInvoiceAlert } from "../PendingInvoiceAlert";
import { ReactComponent as MailIcon } from "../AddonCard/assets/mail.svg";
import { ReactComponent as ArrowRightIcon } from "../../assets/arrow-right.svg";
import { TOPUP_OPTIONS } from "./consts";
import { GetExtraCreditsDialogContent } from "./GetExtraCreditsDialogContent";

interface GetExtraCreditsCardProps {
  pendingInvoice?: PendingInvoice;
  disabled?: boolean;
  onRequestSwitch?: () => void;
}

export type GetExtraCreditsCardHandle = { open: () => void };

export const GetExtraCreditsCard = forwardRef<
  GetExtraCreditsCardHandle,
  GetExtraCreditsCardProps
>(({ pendingInvoice, disabled, onRequestSwitch }, ref) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entity } = useEntity();
  const {
    hasPaymentMethod,
    updateCardDetails,
    isExtraCreditsDialogRequested,
    clearExtraCreditsDialogRequest,
    topupCredits,
  } = useSubscriptionV2();

  const [isOpen, setIsOpen] = useState(false);
  const selectedCreditsRef = useRef(TOPUP_OPTIONS[0].credits);

  const handleOpen = useCallback(async () => {
    if (!hasPaymentMethod) {
      const cardResult = await updateCardDetails();
      if (cardResult.result !== "success") return;
    }
    selectedCreditsRef.current = TOPUP_OPTIONS[0].credits;
    setIsOpen(true);
  }, [hasPaymentMethod, updateCardDetails]);

  useEffect(() => {
    if (isExtraCreditsDialogRequested) {
      handleOpen();
      clearExtraCreditsDialogRequest();
    }
  }, [
    isExtraCreditsDialogRequested,
    handleOpen,
    clearExtraCreditsDialogRequest,
  ]);

  useImperativeHandle(ref, () => ({ open: handleOpen }), [handleOpen]);

  const handleCreditsChange = useCallback((credits: number) => {
    selectedCreditsRef.current = credits;
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    const option = TOPUP_OPTIONS.find(
      (o) => o.credits === selectedCreditsRef.current
    );
    if (!option || !entity) return;

    setIsOpen(false);
    try {
      await topupCredits({
        amountInCents: option.amountInCents,
        onSuccess: () => {
          toast(
            t({
              id: "cx-plans-page.get-extra-credits.success",
              defaultMessage: "Credits purchased successfully!",
            }),
            { variant: "success" }
          );
        },
        onOpenInvoice: (invoiceUrl) => {
          window.open(invoiceUrl, "_blank");
        },
      });
    } catch {
      // Global error toast handles this
    }
  }, [entity, topupCredits, toast, t]);

  return (
    <>
      <CreditCardBase
        icon={<MailIcon className="shrink-0" />}
        title={t({
          id: "cx-plans-page.get-extra-credits-card.title",
          defaultMessage: "Get Extra Credits",
        })}
        description={t({
          id: "cx-plans-page.get-extra-credits-card.description",
          defaultMessage:
            "Get additional credits immediately, with one-time purchase",
        })}
        price="$40"
        priceLabel={t({
          id: "cx-plans-page.get-extra-credits-card.price-label",
          defaultMessage: "per 1,000 message credits",
        })}
        rightContent={
          <Button
            variant="secondary"
            size="sm"
            iconEnd={<ArrowRightIcon />}
            isDisabled={disabled}
            onClick={disabled ? undefined : handleOpen}
          >
            {t({
              id: "cx-plans-page.get-extra-credits-card.button",
              defaultMessage: "Get Credits",
            })}
          </Button>
        }
        footerAlert={
          pendingInvoice && (
            <PendingInvoiceAlert
              text={t({
                id: "cx-plans-page.get-extra-credits-card.pending-invoice-alert",
                defaultMessage:
                  "Payment require additional action ⚠️ Check to restore extra credits",
              })}
              onCompletePayment={() =>
                window.open(pendingInvoice.invoice_url, "_blank")
              }
            />
          )
        }
      />
      <Alert
        isOpen={isOpen}
        onRequestClose={handleClose}
        title={t({
          id: "cx-plans-page.get-extra-credits-dialog.title",
          defaultMessage: "⭐ Get Extra Credits",
        })}
        textAlignment="left"
        content={
          <GetExtraCreditsDialogContent
            onChange={handleCreditsChange}
            onRequestSwitch={
              onRequestSwitch
                ? () => {
                    setIsOpen(false);
                    onRequestSwitch();
                  }
                : undefined
            }
          />
        }
        buttons={[
          {
            text: t({
              id: "cx-plans-page.get-extra-credits-dialog.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "cx-plans-page.get-extra-credits-dialog.confirm",
              defaultMessage: "Get Credits",
            }),
            color: "primary",
            onClick: () => handleConfirm(),
          },
        ]}
      />
    </>
  );
});

GetExtraCreditsCard.displayName = "GetExtraCreditsCard";
